import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ArchiveArticleOption } from "../api/labArchive";

type Props = {
  articles: ArchiveArticleOption[];
  value: string;
  /** The name to show while the list is still on its way, so arriving from a lot does not show a bare code. */
  fallbackName?: string;
  onChange: (erpProductId: string, name: string) => void;
  disabled?: boolean;
};

/**
 * Searchable article picker for the lab archive, same pattern as CompanyCombobox.
 *
 * <p>Matches on name and on code, because both are how the article is known: the lab says „sojina sačma"
 * and the ERP says 12557, and the reader coming from a delivery note has the second one. The count beside
 * each row is what makes the list usable — 586 articles sorted by name say nothing about which of them the
 * lab actually works on, and the one being looked for is almost always among the measured ones.
 */
export function ArticleCombobox({ articles, value, fallbackName, onChange, disabled }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(
    () => articles.find((a) => a.erpProductId === value) ?? null,
    [articles, value],
  );

  const LIMIT = 50;

  /**
   * Pogodci, i koliko ih je ukupno.
   *
   * Spisak se seče na pedeset, ali odsečeno se mora videti: bez toga birač nad 586 artikala izgleda kao
   * da ih ima pedeset, i čovek odustane umesto da ukuca ime.
   */
  const { filtered, ukupnoPogodaka } = useMemo(() => {
    const q = query.trim().toLowerCase();
    const svi = !q
      ? articles
      : articles.filter(
          (a) => (a.name ?? "").toLowerCase().includes(q) || a.erpProductId.toLowerCase().includes(q),
        );
    return { filtered: svi.slice(0, LIMIT), ukupnoPogodaka: svi.length };
  }, [articles, query]);

  useEffect(() => {
    function close(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  useEffect(() => {
    setActiveIdx(0);
  }, [query, open]);

  function pick(a: ArchiveArticleOption) {
    onChange(a.erpProductId, a.name ?? "");
    setQuery("");
    setOpen(false);
  }

  function clear() {
    onChange("", "");
    setQuery("");
    inputRef.current?.focus();
  }

  function onKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActiveIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && open && filtered[activeIdx]) {
      e.preventDefault();
      pick(filtered[activeIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  // A chosen article whose row has not arrived yet still has to read as chosen, so the name from the
  // address is used until the list lands, and the bare code only when there is no name at all.
  const izabranoIme = selected?.name || fallbackName || value;

  return (
    <div ref={wrapRef} style={{ position: "relative", minWidth: 230 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "6px 8px",
          border: "1px solid var(--border)",
          borderRadius: 6,
          background: disabled ? "var(--surface-2)" : "var(--surface)",
          cursor: disabled ? "not-allowed" : "text",
          opacity: disabled ? 0.8 : 1,
        }}
      >
        {value && !open ? (
          <span
            title={izabranoIme}
            style={{ flex: 1, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            <b>{izabranoIme}</b>
          </span>
        ) : (
          <input
            ref={inputRef}
            type="text"
            value={query}
            disabled={disabled}
            onFocus={() => !disabled && setOpen(true)}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onKeyDown={onKey}
            placeholder={t("lab.filter.articlePick", "Artikal: svi")}
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              fontSize: 14,
              fontFamily: "inherit",
              background: "transparent",
              minWidth: 0,
            }}
            aria-autocomplete="list"
            aria-expanded={open}
            role="combobox"
            aria-label={t("lab.filter.articlePick", "Artikal: svi")}
          />
        )}
        {value && !disabled && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              clear();
            }}
            aria-label={t("lab.filter.clearArticle", "Prikaži sve artikle")}
            style={{
              background: "transparent",
              border: "none",
              color: "var(--ink-3)",
              fontSize: 16,
              cursor: "pointer",
              padding: 2,
            }}
          >
            ×
          </button>
        )}
        <span style={{ color: "var(--ink-4)", fontSize: 12 }} aria-hidden>
          ▾
        </span>
      </div>
      {open && (
        <div
          role="listbox"
          className="dd-scroll"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            // Wider than the control on purpose. Two articles here differ only in the packaging at the
            // very end of the name — „(1000/1)" against „(25/1)" — so a list that cuts the end makes
            // them one entry with two counts. It stays inside the viewport on a narrow screen.
            minWidth: "100%",
            width: "max-content",
            maxWidth: "min(620px, 90vw)",
            maxHeight: 320,
            overflowY: "auto",
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: 6,
            boxShadow: "var(--shadow-2)",
            zIndex: 20,
          }}
        >
          {ukupnoPogodaka > filtered.length && (
            <div
              style={{
                padding: "6px 10px", fontSize: 11, color: "var(--ink-3)",
                borderBottom: "1px solid var(--border)", position: "sticky", top: 0,
                background: "var(--surface)",
              }}
            >
              {t("company.pick.cut", "Prikazano {{prikazano}} od {{svih}} — ukucajte ime da suzite.", {
                prikazano: filtered.length,
                svih: ukupnoPogodaka,
              })}
            </div>
          )}
          {filtered.length === 0 ? (
            <div style={{ padding: 10, fontSize: 13, color: "var(--ink-3)" }}>
              {t("lab.filter.articleNoMatch", "Nema artikla po toj reči.")}
            </div>
          ) : (
            filtered.map((a, i) => (
              <div
                key={a.erpProductId}
                role="option"
                aria-selected={a.erpProductId === value}
                onMouseEnter={() => setActiveIdx(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  pick(a);
                }}
                style={{
                  padding: "8px 10px",
                  cursor: "pointer",
                  fontSize: 14,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 8,
                  background: i === activeIdx ? "var(--surface-2)" : "transparent",
                }}
              >
                <span style={{ fontWeight: 600, flex: 1, lineHeight: 1.3, overflowWrap: "anywhere" }}>
                  {a.name || a.erpProductId}
                </span>
                <span className="tag neutral" style={{ fontSize: 11, flexShrink: 0, whiteSpace: "nowrap" }}>
                  {t("lab.filter.articleCount", "{{n}} analiza", { n: a.analyses })}
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
